import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Crown, ArrowLeft, Loader2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";
import { useToast } from "@/hooks/use-toast";
import apiClient from "@/api/ApiClient";
import { isDebug } from "@/config/env";
import { useCapacitorPlatform } from "@/hooks/use-capacitor";

interface SubscriptionPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedPlan: 'monthly' | 'yearly';
  onPurchaseSubscription: (plan: 'monthly' | 'yearly', paymentMethod: string) => void;
}

export const SubscriptionPaymentModal = ({
  open,
  onOpenChange,
  selectedPlan,
  onPurchaseSubscription
}: SubscriptionPaymentModalProps) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { toast } = useToast();
  const capacitorPlatform = useCapacitorPlatform();
  const isNative = capacitorPlatform === 'ios' || capacitorPlatform === 'android';
  const [isProcessing, setIsProcessing] = useState(false);

  const planDetails = {
    monthly: { price: 9.99, originalPrice: 19.99, billing: 'per month' },
    yearly: { price: 79.99, originalPrice: 159.99, billing: 'per year' }
  };

  const plan = planDetails[selectedPlan];

  const handleCompletePayment = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    console.log("handleCompletePayment called!", { isProcessing, isDebug, isNative });
    
    if (isProcessing) {
      console.log("Already processing, ignoring click");
      return; // Prevent double clicks
    }
    
    setIsProcessing(true);
    
    // In debug mode, use Stripe (not bypass) - debug bypass is handled by special buttons
    // This function always uses Stripe, even in debug mode

    // Check if running in Capacitor (native mobile app)
    if (isNative) {
      // TODO: Implement RevenueCat subscription purchase for mobile
      // For now, show message that mobile payments are coming soon
      console.log('Mobile subscription via RevenueCat - to be implemented');
      alert('Mobile subscriptions via RevenueCat will be available soon. Please use web version for now.');
      return;
    }

    // Use Stripe Checkout for web - redirect directly to Stripe Checkout
    try {
      const successUrl = window.location.origin + "/payment/success?session_id={CHECKOUT_SESSION_ID}";
      const cancelUrl = window.location.origin + "/payment/cancel";
      
      console.log("Creating subscription checkout session...", { selectedPlan, platform: "web" });
      
      const response = await apiClient.createSubscriptionCheckout(
        selectedPlan,
        "web",
        successUrl,
        cancelUrl
      );

      console.log("Checkout session response:", response);

      if (response && response.success && response.url) {
        console.log("Redirecting to Stripe Checkout:", response.url);
        // Redirect to Stripe Checkout - don't reset isProcessing, we're leaving the page
        window.location.href = response.url;
        return; // Don't reset isProcessing, we're redirecting
      } else {
        const errorMessage = response?.message || "Failed to create checkout session. Please check Stripe configuration (Price IDs must be set in backend).";
        console.error("Failed to create subscription checkout session:", errorMessage, response);
        toast({
          title: "Payment Error",
          description: errorMessage,
          variant: "destructive",
        });
        setIsProcessing(false);
      }
    } catch (error: any) {
      console.error("Error creating subscription checkout session:", error);
      const errorMessage = error?.message || error?.response?.data?.message || "Failed to start payment. Please check backend logs and Stripe configuration.";
      toast({
        title: "Payment Error",
        description: errorMessage,
        variant: "destructive",
      });
      setIsProcessing(false);
    }
  };

  const handleClose = (open: boolean) => {
    console.log("SubscriptionPaymentModal handleClose called", { open });
    if (!open && isProcessing) {
      console.log("Modal closing while processing - this might be a problem");
    }
    onOpenChange(open);
  };

  console.log("SubscriptionPaymentModal render", { open, isProcessing, selectedPlan, isMobile, isDebug, isNative });
  
  if (isMobile) {
    return (
      <>
        {/* Mobile: Full Page Payment Method Selection */}
        {open && (
          <div className="fixed inset-0 z-[100] bg-background">
            <div className="flex flex-col h-full">
              <div className="flex items-center p-4 border-b">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => onOpenChange(false)}
                  className="mr-3"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <h1 className="text-lg font-semibold">{t('settings.complete_your_upgrade')}</h1>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {/* Selected Plan Summary */}
                <Card className="p-4 bg-gradient-romantic text-white border-0">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Crown className="h-5 w-5" />
                      <span className="text-lg font-semibold">
                        {selectedPlan === 'monthly' ? t('premium_modal.monthly') : t('premium_modal.yearly')} {t('premium_modal.premium')} {selectedPlan === 'monthly' ? t('premium_modal.monthly_plan') : t('premium_modal.yearly_plan')}
                      </span>
                    </div>
                    <div className="space-y-1">
                      <div className="text-sm line-through opacity-75">${plan.originalPrice}</div>
                      <div className="text-3xl font-bold">${plan.price}</div>
                      <div className="text-sm opacity-90">{selectedPlan === 'monthly' ? t('premium_modal.per_month') : t('premium_modal.per_year')}</div>
                      <div className="text-sm font-medium text-green-200">
                        {t('premium_modal.save_50')}{selectedPlan === 'yearly' ? ' + ' + t('premium_modal.two_months_free') : ''}
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Payment Method Selection */}
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-center">{t('settings.payment_method')}</h3>
                  <div className="space-y-3">
                    <Card 
                      className="p-4 border-2 border-primary bg-primary/10"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center">
                          <span className="text-white text-sm font-bold">💳</span>
                        </div>
                        <div>
                          <h3 className="font-semibold">{t('settings.credit_debit_card')}</h3>
                          <p className="text-sm text-muted-foreground">{t('settings.pay_with_card')}</p>
                        </div>
                      </div>
                    </Card>

                  </div>
                </div>
              </div>

              <div className="p-4 border-t z-50">
                <Button 
                  type="button"
                  variant="romantic" 
                  className="w-full"
                  onClick={(e) => {
                    console.log("MOBILE BUTTON CLICKED!", e);
                    e.preventDefault();
                    e.stopPropagation();
                    handleCompletePayment(e);
                  }}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('common.loading') || 'Processing...'}
                    </>
                  ) : (
                    <>
                      <span className="mr-2">💳</span>
                      {t('settings.continue_with_card_payment')}
                    </>
                  )}
                </Button>
                
                {/* Debug buttons - only show in debug mode */}
                {isDebug && (
                  <div className="flex gap-2 mt-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log("DEBUG: Simulating successful payment");
                        onPurchaseSubscription(selectedPlan, 'card');
                        onOpenChange(false);
                      }}
                    >
                      ✅ Debug: Success
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log("DEBUG: Simulating failed payment");
                        toast({
                          title: "Payment Failed (Debug)",
                          description: "Simulated payment failure",
                          variant: "destructive",
                        });
                      }}
                    >
                      ❌ Debug: Fail
                    </Button>
                  </div>
                )}
                
                <div className="text-xs text-muted-foreground text-center mt-2">
                  {t('premium_modal.secure_payment')}
                </div>
              </div>
            </div>
          </div>
        )}

      </>
    );
  }

  return (
    <>
      {/* Desktop: Payment Method Selection Modal */}
      <Dialog open={open} onOpenChange={(open) => {
        console.log("Dialog onOpenChange called directly", { open, isProcessing });
        if (!open && isProcessing) {
          console.warn("Dialog trying to close while processing - preventing close");
          return; // Don't close if processing
        }
        handleClose(open);
      }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-center gap-2 text-xl">
              <Crown className="h-6 w-6 text-amber-500" />
              {t('settings.complete_your_upgrade')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Selected Plan Summary */}
            <Card className="p-4 bg-gradient-romantic text-white border-0">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Crown className="h-5 w-5" />
                  <span className="text-lg font-semibold">
                    {selectedPlan === 'monthly' ? t('premium_modal.monthly') : t('premium_modal.yearly')} {t('premium_modal.premium')} {selectedPlan === 'monthly' ? t('premium_modal.monthly_plan') : t('premium_modal.yearly_plan')}
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="text-sm line-through opacity-75">${plan.originalPrice}</div>
                  <div className="text-3xl font-bold">${plan.price}</div>
                  <div className="text-sm opacity-90">{selectedPlan === 'monthly' ? t('premium_modal.per_month') : t('premium_modal.per_year')}</div>
                  <div className="text-sm font-medium text-green-200">
                    {t('premium_modal.save_50')}{selectedPlan === 'yearly' ? ' + ' + t('premium_modal.two_months_free') : ''}
                  </div>
                </div>
              </div>
            </Card>

            {/* Payment Method Selection */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-center">{t('settings.payment_method')}</h3>
              <div className="space-y-3">
                <Card 
                  className="p-4 border-2 border-primary bg-primary/10"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-500 rounded flex items-center justify-center">
                      <span className="text-white text-sm font-bold">💳</span>
                    </div>
                    <div>
                      <h3 className="font-semibold">{t('settings.credit_debit_card')}</h3>
                      <p className="text-sm text-muted-foreground">{t('settings.pay_with_card')}</p>
                    </div>
                  </div>
                </Card>

              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button 
                type="button"
                variant="romantic" 
                className="w-full"
                onClick={(e) => {
                  console.log("BUTTON CLICKED!", e);
                  e.preventDefault();
                  e.stopPropagation();
                  handleCompletePayment(e);
                }}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('common.loading') || 'Processing...'}
                  </>
                ) : (
                  <>
                    <span className="mr-2">💳</span>
                    {t('settings.continue_with_card_payment')}
                  </>
                )}
              </Button>
              
              {/* Debug buttons - only show in debug mode */}
              {isDebug && (
                <div className="flex gap-2 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log("DEBUG: Simulating successful payment");
                      onPurchaseSubscription(selectedPlan, 'card');
                      onOpenChange(false);
                    }}
                  >
                    ✅ Debug: Success
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log("DEBUG: Simulating failed payment");
                      toast({
                        title: "Payment Failed (Debug)",
                        description: "Simulated payment failure",
                        variant: "destructive",
                      });
                    }}
                  >
                    ❌ Debug: Fail
                  </Button>
                </div>
              )}
              
              <div className="text-xs text-muted-foreground text-center">
                {t('premium_modal.secure_payment')}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </>
  );
};